import { useCallback, useEffect, useRef, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import "./code-editor-shell.scss";

/**
 * The same grip the designer's right panel uses: a thin hit area whose edge line turns blue
 * while you drag, with the grab button appearing on hover. "vertical" drags left/right and
 * sizes the right column; "horizontal" drags up/down and sizes the result drawer.
 */
export interface ICodeEditorSplitterProps {
  orientation: "vertical" | "horizontal";
  /** The current width (vertical) or height (horizontal) of the panel this grip sizes. */
  size: number;
  onSizeChange: (size: number) => void;
  minSize: number;
  maxSize: number;
  /** Which side of the grip the panel it sizes is on; defaults to "after". */
  panel?: "before" | "after";
  /**
   * When given, the grip also carries the fold control — like the designer's right panel,
   * where collapse lives on the divider instead of sitting in a corner.
   */
  isCollapsed?: boolean;
  onToggleCollapsed?: (isCollapsed: boolean) => void;
}

export const CodeEditorSplitter = (props: ICodeEditorSplitterProps): JSX.Element => {
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const start = useRef<number>(0);
  const startSize = useRef<number>(props.size);

  const isVertical: boolean = props.orientation === "vertical";

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (props.isCollapsed) return;
      event.preventDefault();
      start.current = isVertical ? event.clientX : event.clientY;
      startSize.current = props.size;
      // Set here, not in the effect: by the time the effect runs the browser has already
      // started selecting text under the cursor.
      document.body.style.cursor = isVertical ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
      setIsResizing(true);
    },
    [isVertical, props.size, props.isCollapsed]
  );

  useEffect((): (() => void) | undefined => {
    if (!isResizing) return undefined;

    const handleMouseMove = (event: MouseEvent): void => {
      // Dragging towards the panel shrinks it, away from it grows it.
      const delta: number = (isVertical ? event.clientX : event.clientY) - start.current;
      const next: number = startSize.current + (props.panel === "before" ? delta : -delta);
      props.onSizeChange(Math.min(Math.max(next, props.minSize), props.maxSize));
    };
    const handleMouseUp = (): void => {
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // A drag that grazed some text leaves it highlighted behind the panel.
      window.getSelection()?.removeAllRanges();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return (): void => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, isVertical, props]);

  return (
    <div
      className={`kuika__code-editor__resize kuika__code-editor__resize--${props.orientation}${isResizing ? " resizing" : ""}${props.isCollapsed ? " collapsed" : ""}`}
      role="separator"
      aria-orientation={isVertical ? "vertical" : "horizontal"}
      aria-label={isVertical ? "Resize the Kuika AI panel" : "Resize the result drawer"}
      onMouseDown={handleMouseDown}
      data-testid={`codeEditorSplitter-${props.orientation}`}
    >
      {props.onToggleCollapsed && (
        <div
          className="kuika__code-editor__resize__collapse"
          onMouseDown={(event: React.MouseEvent): void => event.stopPropagation()}
        >
          <KuikaButton
            type="secondary"
            size="md"
            isIcon
            aria-label={props.isCollapsed ? "Show the panel" : "Hide the panel"}
            style={{ boxShadow: "var(--shadow-sm)" }}
            onClick={(event?: React.MouseEvent): void => {
              event?.stopPropagation();
              props.onToggleCollapsed?.(!props.isCollapsed);
            }}
            data-testid={props.isCollapsed ? "showParametersPanelButton" : "hideParametersPanelButton"}
          >
            <KuikaSvg
              name={props.isCollapsed ? "layout-sidebar-left-expand" : "layout-sidebar-left-collapse"}
              type="sidebar-icons"
              width={16}
              height={16}
              pointer
              // The icons are drawn for a LEFT panel: mirror them for the right column, and
              // turn them a quarter so the drawer's arrow points down to close, up to open.
              style={{ transform: isVertical ? "scaleX(-1)" : "rotate(-90deg)" }}
            />
          </KuikaButton>
        </div>
      )}

      {!props.isCollapsed && (
        <KuikaButton
          type="secondary"
          size="md"
          isIcon
          aria-label={isVertical ? "Drag to resize the panel" : "Drag to resize the drawer"}
          style={{ boxShadow: "var(--shadow-sm)" }}
        >
          <KuikaSvg
            name="arrows-horizontal"
            type="common"
            width={16}
            height={16}
            style={isVertical ? undefined : { transform: "rotate(90deg)" }}
          />
        </KuikaButton>
      )}
    </div>
  );
};
